import React from "react";
import { AlertTriangle } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { Button } from "@/components/ui/button";
import { operatorErrorMessage } from "./errorCopy";

/**
 * The ONE failed-load card for every city-desk list (R10c H1-2). A fetch that fails must say so and
 * offer a retry - never fall through to the empty state, because "Nothing is waiting" after a 422
 * tells the operator the queue is clear while reviews wait.
 *
 * Usage: `if (error) return <ListError error={error} onRetry={load} />` BEFORE the empty branch.
 * `error` is the thrown exception (mapped to translated copy, never the server's text); pass
 * `message` instead to show a fixed string. `testId` defaults to "list-error".
 */
export function ListError({ error, message, onRetry, testId = "list-error", className = "" }) {
  const { t } = useApp();
  const text = message || operatorErrorMessage(error, t);
  return (
    <div
      role="alert"
      className={`flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive ${className}`}
      data-testid={testId}
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block font-semibold">{t("operator.errors.loadFailedTitle")}</span>
        <span className="block">{text}</span>
      </span>
      {onRetry && (
        <Button size="sm" variant="outline" className="min-h-[44px] rounded-full sm:min-h-0" data-testid={`${testId}-retry`} onClick={onRetry}>
          {t("common.retry")}
        </Button>
      )}
    </div>
  );
}
